import { defineStore } from 'pinia'
import axios from 'axios'

// Authoring state for the ALLDOQ user guides. Ground Control owns the writing
// and reviewing; AlldoqExchange owns delivery, and receives a whole set at
// publish time rather than a page at a time.
export const useManualsStore = defineStore('manuals', {
  state: () => ({
    sets: [],
    currentSet: null,
    pages: [],
    page: null,
    images: [],
    revisions: [],
    publications: [],
    suggestions: [],
    suggestionCounts: {},
    suggestion: null,
    // Gap analysis: the runs for the set on screen, the one in flight, and
    // whether the server can reach the model at all.
    analyses: [],
    analysis: null,
    analysisConfigured: true,
    loading: false,
    saving: false,
    error: null
  }),

  getters: {
    // A slot the changelog job could re-shoot. Anything uploaded by hand has no
    // recipe and must be left alone, or a later regeneration would quietly
    // replace a deliberate choice.
    regenerableImages: (state) => state.images.filter((i) => i.regenerable),

    // Redaction is the reason to look twice at a slot: an unmasked capture of a
    // screen that shows addresses is how real data reaches a client document.
    unredactedImages: (state) => state.images.filter((i) => !i.redactions?.length),

    // Rules entered but never applied to the picture. Publishing refuses while
    // any of these exist, so surfacing them is what makes that refusal fixable
    // rather than mysterious.
    staleImages: (state) => state.images.filter((i) => i.render_stale)
  },

  actions: {
    async fetchSets() {
      this.loading = true
      this.error = null
      try {
        const { data } = await axios.get('/api/v1/manual_sets')
        this.sets = data.manual_sets
      } catch (e) {
        this.error = e.response?.data?.error || 'Could not load the manual sets'
      } finally {
        this.loading = false
      }
    },

    async publish(key) {
      this.saving = true
      this.error = null
      try {
        const { data } = await axios.post(`/api/v1/manual_sets/${key}/publish`)
        this.sets = this.sets.map((s) => (s.key === key ? data.manual_set : s))
        return data.publication
      } catch (e) {
        this.error = e.response?.data?.errors?.join(', ') || 'Publish failed'
        return null
      } finally {
        this.saving = false
      }
    },

    async fetchSet(key) {
      this.loading = true
      this.error = null
      try {
        const { data } = await axios.get(`/api/v1/manual_sets/${key}`)
        this.currentSet = data.manual_set
        this.pages = data.pages
        this.publications = data.publications || []
      } catch (e) {
        this.error = e.response?.data?.error || 'Could not load that set'
      } finally {
        this.loading = false
      }
    },

    async saveReviewers(key, emails) {
      this.saving = true
      try {
        const { data } = await axios.patch(`/api/v1/manual_sets/${key}`, {
          manual_set: { reviewer_emails: emails }
        })
        this.sets = this.sets.map((s) => (s.key === key ? data.manual_set : s))
        if (this.currentSet?.key === key) this.currentSet = data.manual_set
        return true
      } catch (e) {
        this.error = e.response?.data?.errors?.join(', ') || 'Could not save the reviewers'
        return false
      } finally {
        this.saving = false
      }
    },

    // The runs for a set, and whichever one is still going. Called on opening
    // the set and then repeatedly while something is running.
    async fetchAnalyses(setKey) {
      try {
        const { data } = await axios.get('/api/v1/manual_analyses', { params: { set: setKey } })
        this.analyses = data.analyses
        this.analysis = data.current
        this.analysisConfigured = data.configured
        return data.current
      } catch (e) {
        this.error = e.response?.data?.error || 'Could not load the analysis history'
        return null
      }
    },

    // Queues a run. It returns as soon as the row exists, because the work
    // itself takes minutes and is watched rather than waited on.
    //
    // Without `page`, the run covers every guide and reads the commits nobody has
    // read yet. With one, it covers that guide alone and reads from that guide's
    // own last check: worth starting from a guide row when a set-wide check came
    // back quiet and a person still distrusts one particular page, which is the
    // case the audit says a set-wide run answers badly.
    async startAnalysis(setKey, { dryRun = false, page = null } = {}) {
      this.saving = true
      this.error = null
      try {
        const { data } = await axios.post('/api/v1/manual_analyses', {
          set: setKey, dry_run: dryRun ? '1' : '', page: page || ''
        })
        this.analysis = data.analysis
        this.analyses = [data.analysis, ...this.analyses.filter((a) => a.id !== data.analysis.id)]
        return data.analysis
      } catch (e) {
        // A run already going is not an error worth a red box: show what it is.
        if (e.response?.status === 409 && e.response.data?.analysis) {
          this.analysis = e.response.data.analysis
        }
        this.error = e.response?.data?.errors?.join(', ') || 'Could not start the analysis'
        return null
      } finally {
        this.saving = false
      }
    },

    // Whether the check in flight was started for this guide, so the row can show
    // its own progress and not claim to be working when the run is about another
    // page. The screen still refreshes the list when a run finishes, so a guide
    // whose check landed while the tab sat open is corrected within 15 seconds.
    runningGuide(page) {
      return !!this.analysis?.running && this.analysis.manual_page_id === page.id
    },


    async createPage(setKey, attrs) {
      this.saving = true
      try {
        const { data } = await axios.post('/api/v1/manual_pages', { set: setKey, page: attrs })
        this.pages = [...this.pages, data.page]
        return data.page
      } catch (e) {
        this.error = e.response?.data?.errors?.join(', ') || 'Could not add the guide'
        return null
      } finally {
        this.saving = false
      }
    },

    async deletePage(id) {
      this.saving = true
      try {
        const { data } = await axios.delete(`/api/v1/manual_pages/${id}`)
        this.pages = data.pages
        return true
      } catch (e) {
        this.error = e.response?.data?.errors?.join(', ') || 'Could not remove the guide'
        return false
      } finally {
        this.saving = false
      }
    },

    async reorderPages(setKey, slugs) {
      const previous = this.pages
      try {
        const { data } = await axios.patch(`/api/v1/manual_sets/${setKey}/reorder`, { order: slugs })
        this.pages = data.pages
        return true
      } catch (e) {
        this.pages = previous
        this.error = 'Could not save the new order'
        return false
      }
    },

    async restoreRevision(pageId, revisionId) {
      this.saving = true
      try {
        const { data } = await axios.post(`/api/v1/manual_pages/${pageId}/restore`, {
          revision_id: revisionId
        })
        this.page = { ...this.page, ...data.page }
        this.revisions = data.revisions
        return true
      } catch (e) {
        this.error = e.response?.data?.errors?.join(', ') || 'Could not restore that version'
        return false
      } finally {
        this.saving = false
      }
    },

    async fetchSuggestions(params = {}) {
      this.loading = true
      this.error = null
      try {
        const { data } = await axios.get('/api/v1/manual_suggestions', { params })
        this.suggestions = data.suggestions
        this.suggestionCounts = data.counts
      } catch (e) {
        this.error = e.response?.data?.error || 'Could not load the review queue'
      } finally {
        this.loading = false
      }
    },

    async fetchSuggestion(id) {
      this.error = null
      try {
        const { data } = await axios.get(`/api/v1/manual_suggestions/${id}`)
        this.suggestion = data.suggestion
        return data.suggestion
      } catch (e) {
        this.error = 'Could not load that suggestion'
        return null
      }
    },

    // Approving applies the change to the manual. It does not publish it, so a
    // reviewer accepting wording is not also deciding readers see it now.
    async approveSuggestion(id, edits = null) {
      this.saving = true
      this.error = null
      try {
        const body = edits ? { suggestion: edits } : {}
        const { data } = await axios.post(`/api/v1/manual_suggestions/${id}/approve`, body)
        this.replaceSuggestion(data.suggestion)
        return true
      } catch (e) {
        this.error = e.response?.data?.errors?.join(', ') || 'Could not approve that change'
        return false
      } finally {
        this.saving = false
      }
    },

    async rejectSuggestion(id, reason) {
      this.saving = true
      this.error = null
      try {
        const { data } = await axios.post(`/api/v1/manual_suggestions/${id}/reject`, { reason })
        this.replaceSuggestion(data.suggestion)
        return true
      } catch (e) {
        this.error = e.response?.data?.errors?.join(', ') || 'Could not reject that change'
        return false
      } finally {
        this.saving = false
      }
    },

    async saveSuggestion(id, attrs) {
      this.saving = true
      try {
        const { data } = await axios.patch(`/api/v1/manual_suggestions/${id}`, { suggestion: attrs })
        this.replaceSuggestion(data.suggestion)
        return true
      } catch (e) {
        this.error = e.response?.data?.errors?.join(', ') || 'Could not save that edit'
        return false
      } finally {
        this.saving = false
      }
    },

    replaceSuggestion(next) {
      this.suggestions = this.suggestions.map((s) => (s.id === next.id ? { ...s, ...next } : s))
      if (this.suggestion?.id === next.id) this.suggestion = next
    },

    async fetchPage(id) {
      this.loading = true
      this.error = null
      try {
        const { data } = await axios.get(`/api/v1/manual_pages/${id}`)
        this.page = data.page
        this.images = data.images
        this.revisions = data.revisions
      } catch (e) {
        this.error = e.response?.data?.error || 'Could not load that page'
      } finally {
        this.loading = false
      }
    },

    // Visual refresh: ask for this guide's shootable screenshots to be re-taken.
    // Nothing is photographed here. The request is recorded against the slots and
    // the payload carries the capture command that honours it, because the run
    // needs a real browser signed in to ALLDOQ (docs/manuals.md).
    async requestCaptures (pageId, { cancel = false } = {}) {
      this.saving = true
      this.error = null
      try {
        const { data } = await axios.post(`/api/v1/manual_pages/${pageId}/request_captures`,
                                         cancel ? { cancel: '1' } : {})
        const merge = (p) => (p.id === data.page.id ? { ...p, ...data.page } : p)
        this.pages = this.pages.map(merge)
        if (this.page?.id === data.page.id) this.page = { ...this.page, ...data.page }
        // The count shown against the set is the queue across its guides, so it
        // moves with the row rather than waiting for a reload. Only recomputed
        // when the loaded pages really are that whole set: the guide screen can be
        // opened with another set's pages still in the store.
        const setId = data.page.manual_set_id
        if (this.pages.length && this.pages.every((p) => p.manual_set_id === setId)) {
          const queued = this.pages.reduce((n, p) => n + (p.capture?.requested || 0), 0)
          this.sets = this.sets.map((s) => (s.id === setId ? { ...s, capture_requests: queued } : s))
        }
        return data
      } catch (e) {
        this.error = e.response?.data?.errors?.join(', ') || 'Could not queue the screenshots'
        return null
      } finally {
        this.saving = false
      }
    },

    async publishPage(id) {
      this.saving = true
      this.error = null
      try {
        const { data } = await axios.post(`/api/v1/manual_pages/${id}/publish`)
        return data.publication
      } catch (e) {
        this.error = e.response?.data?.errors?.join(', ') || 'Publish failed'
        return null
      } finally {
        this.saving = false
      }
    },

    async savePage(id, attrs, note = null) {
      this.saving = true
      this.error = null
      try {
        const { data } = await axios.patch(`/api/v1/manual_pages/${id}`, { page: attrs, note })
        this.page = { ...this.page, ...data.page }
        return true
      } catch (e) {
        this.error = e.response?.data?.errors?.join(', ') || 'Could not save'
        return false
      } finally {
        this.saving = false
      }
    },

    async saveImage(id, attrs) {
      this.saving = true
      try {
        const { data } = await axios.patch(`/api/v1/manual_images/${id}`, { image: attrs })
        this.images = this.images.map((i) => (i.id === id ? data.image : i))
        return true
      } catch (e) {
        this.error = e.response?.data?.errors?.join(', ') || 'Could not save the image'
        return false
      } finally {
        this.saving = false
      }
    },

    // The flattened image: capture, cropped, with the masks painted in. Sent
    // after the rules are saved, because the server stamps it with the
    // fingerprint of the rules it currently holds.
    async renderImage(id, blob, filename) {
      const form = new FormData()
      form.append('file', blob, filename)
      try {
        const { data } = await axios.post(`/api/v1/manual_images/${id}/rendered`, form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        this.images = this.images.map((i) => (i.id === id ? data.image : i))
        return true
      } catch (e) {
        this.error = e.response?.data?.errors?.join(', ') || 'Could not apply the crop and masks'
        return false
      }
    },

    async replaceImage(id, file) {
      this.saving = true
      const form = new FormData()
      form.append('file', file)
      try {
        const { data } = await axios.post(`/api/v1/manual_images/${id}/replace`, form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        this.images = this.images.map((i) => (i.id === id ? data.image : i))
        return true
      } catch (e) {
        this.error = e.response?.data?.errors?.join(', ') || 'Could not replace the image'
        return false
      } finally {
        this.saving = false
      }
    }
  }
})
